import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Link,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { CheckIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { FormInput, FormDatepicker, FormAutocomplete, FormAutocompleteMultiSelect } from './FormControls';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import SupplierField from './SupplierField';

const buildMapLink = (lat, lng) => (lat != null && lng != null ? `https://www.google.com/maps?q=${lat},${lng}` : '');

// Pulls lat/lng out of common Google Maps link formats, or a raw "lat,lng" string.
const extractLatLng = input => {
  if (!input) return null;
  const trimmed = input.trim();

  let match = trimmed.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (match) return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };

  match = trimmed.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (match) return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };

  match = trimmed.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (match) return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };

  match = trimmed.match(/^(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)$/);
  if (match) return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };

  return null;
};

const isValidLatLng = (lat, lng) =>
  Number.isFinite(lat) && Number.isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

const DieselPurchase = ({ values }) => {
  const { watch, setValue } = useFormContext();

  const spName = watch('spName');
  const fuelLocationDropPin = watch('fuelLocationDropPin');
  const reqStatus = watch('requisitionStatus');

  const [entranceCoords, setEntranceCoords] = useState(null);
  const [isLoadingEntrance, setIsLoadingEntrance] = useState(false);
  const [isResolvingFuelLink, setIsResolvingFuelLink] = useState(false);
  const [copiedField, setCopiedField] = useState(null); // 'entrance' | 'fuel' | null

  const entranceMapLink = buildMapLink(entranceCoords?.lat, entranceCoords?.lng);

  useEffect(() => {
    let cancelled = false;
    const fetchEntrance = async () => {
      if (!spName) {
        setEntranceCoords(null);
        return;
      }
      setIsLoadingEntrance(true);
      try {
        const res = await customAxios.get(`/Ops/GetContracts?contractUniqueID=${spName}`, { headers: authHeader() });
        console.log('res ===', res);
        if (res.status === 200) {
          const response = await customAxios.get(
            `/SiteDetails/GetSiteDetailsMapCoordinates/${res.data[0].siteAssessmentID}`,
            { headers: authHeader() },
          );
          console.log('Response ===', response);

          const entranceData = response?.data?.find(item => item.mapLabel?.startsWith('Entrance'));
          setEntranceCoords({
            lat: entranceData?.latitude,
            lng: entranceData?.longitude,
          });
        }
      } catch (err) {
        console.warn('Failed to fetch entrance coordinates:', err);
        if (!cancelled) setEntranceCoords(null);
      } finally {
        if (!cancelled) setIsLoadingEntrance(false);
      }
    };
    fetchEntrance();
    return () => {
      cancelled = true;
    };
  }, [spName]);

  // Normalizes whatever the backoffice user pastes into Fuel Drop Location
  // into a clean https://www.google.com/maps?q=lat,lng link.
  const handleFuelLinkBlur = async e => {
    const raw = e.target.value?.trim();
    if (!raw) return;

    const coords = extractLatLng(raw);
    if (coords && isValidLatLng(coords.lat, coords.lng)) {
      setValue('fuelLocationDropPin', buildMapLink(coords.lat, coords.lng), { shouldValidate: true });
      return;
    }

    if (/goo\.gl\/maps|maps\.app\.goo\.gl/.test(raw)) {
      setIsResolvingFuelLink(true);
      try {
        const res = await customAxios.get(`/Ops/GetMapCoordinatesFromUrl?googleShortUrl=${raw}`, {
          headers: authHeader(),
        });
        const { latitude, longitude } = res.data;

        if (isValidLatLng(latitude, longitude)) {
          setValue('fuelLocationDropPin', buildMapLink(latitude, longitude), { shouldValidate: true });
        }
      } catch (err) {
        // silently keep original link
      } finally {
        setIsResolvingFuelLink(false);
      }
    }
  };

  const handleCopy = (text, field) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* ─── BASIC INFO & LOCATION ─── */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight="bold">
            Basic Info
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormAutocomplete name="spName" label="SP Name" data={values?.spAutocompleteData || []} />
            </Grid>

            {(reqStatus === 'Ordered' || reqStatus === 'Approved' || reqStatus === 'Delivered') && (
                <SupplierField supplierList={values.supplierList || []} />
            )}

            {/* ─── ENTRANCE + FUEL DROP LOCATION ─── */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                Fuel Map Pin Location
              </Typography>

              <Grid container spacing={2}>
                {/* Site Entrance — always disabled, auto-populated from the selected SP/WH */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Select SP Name to load Site Entrance"
                    value={isLoadingEntrance ? 'Loading...' : entranceMapLink}
                    disabled
                    fullWidth
                    size="small"
                    placeholder={'Select SP Name to load Site Entrance'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {isLoadingEntrance ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Tooltip title={copiedField === 'entrance' ? 'Copied!' : 'Copy link'}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!entranceMapLink}
                                  onClick={() => handleCopy(entranceMapLink, 'entrance')}>
                                  {copiedField === 'entrance' ? (
                                    <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                  ) : (
                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Link
                    href={entranceMapLink || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    underline="hover"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 0.5,
                      color: entranceMapLink ? 'primary.main' : 'text.disabled',
                      pointerEvents: entranceMapLink ? 'auto' : 'none',
                    }}>
                    View on Map <OpenInNewIcon sx={{ fontSize: 12 }} />
                  </Link>
                </Grid>

                {/* Fuel Drop Location — editable, backoffice inserts/edits a Google Maps link */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Fuel Drop Location"
                    value={fuelLocationDropPin || ''}
                    onChange={e => setValue('fuelLocationDropPin', e.target.value, { shouldValidate: true })}
                    onBlur={handleFuelLinkBlur}
                    fullWidth
                    size="small"
                    placeholder="Paste Google Maps link"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {isResolvingFuelLink ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Tooltip title={copiedField === 'fuel' ? 'Copied!' : 'Copy link'}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!fuelLocationDropPin}
                                  onClick={() => handleCopy(fuelLocationDropPin, 'fuel')}>
                                  {copiedField === 'fuel' ? (
                                    <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                  ) : (
                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Link
                    href="https://www.google.com/maps"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    underline="hover"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: 'primary.main' }}>
                    Open Google Maps <OpenInNewIcon sx={{ fontSize: 12 }} />
                  </Link>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ─── DIESEL REQUEST INFO ─── (unchanged) */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight="bold">
            Diesel Request Info
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormDatepicker name="poDate" label="Date" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormInput
                name="litresOrdered"
                label="Litres Ordered"
                placeholder="Enter Litres"
                type="number"
                inputProps={{ step: '0.01' }}
              />
            </Grid>
            {(reqStatus === 'Approved' || reqStatus === 'Delivered') && (
              <Grid item xs={12} sm={4}>
                <FormInput
                  name="litresDelivered"
                  label="Litres Delivered"
                  placeholder="Enter Litres"
                  type="number"
                  inputProps={{ step: '0.01' }}
                />
              </Grid>
            )}
            {(reqStatus === 'Approved' || reqStatus === 'Delivered') && (
              <Grid item xs={12} sm={4}>
                <FormInput
                  name="totalNetValue"
                  label="Total Net Value"
                  placeholder="Enter Value"
                  type="number"
                  inputProps={{ step: '0.01', min: 0 }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <FormInput name="requestorEmail" label="Requestor Email" placeholder="Enter Email" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormAutocompleteMultiSelect
                name="departmentMachine"
                label="Department / Machine"
                data={values?.departmentList || []}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <FormInput
                name="description"
                label="Description"
                placeholder="Enter description"
                multiline
                minRows={4}
                maxRows={10}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default DieselPurchase;
